import { LIMITS } from './basicsModel';

/**
 * Age x gender quota rows: { key, minAge, maxAge, male, female } (strings from inputs).
 * Backend shape (QuotaSettings): { quota, data: [{ gender:{male,female}, age:{minAge,maxAge}, completes }] }.
 * ASSUMPTION: `completes` per row = male + female; `quota` is a free-text label ("age-gender").
 */
let keySeq = 0;
export const newKey = () => `row-${++keySeq}`;

const int = (v) => (/^\d+$/.test(String(v).trim()) ? Number(v) : null);
export const rowTotal = (r) => (int(r.male) || 0) + (int(r.female) || 0);
export const totalCompletes = (rows) => rows.reduce((sum, r) => sum + rowTotal(r), 0);

/** Completes per gender across all rows (invalid cells count as 0). */
export const genderTotals = (rows) => rows.reduce((t, r) => ({ male: t.male + (int(r.male) || 0), female: t.female + (int(r.female) || 0) }), { male: 0, female: 0 });

/** Split `total` into `parts` whole numbers that sum exactly to `total` (largest remainder). */
export const splitEvenly = (total, parts) => {
  const base = Math.floor(total / parts);
  const extra = total - base * parts;
  return Array.from({ length: parts }, (_, i) => base + (i < extra ? 1 : 0));
};

/** Cut [minAge, maxAge] into up to `bands` contiguous, non-overlapping age bands. */
export const ageBands = (minAge, maxAge, bands = 4) => {
  const span = maxAge - minAge + 1;
  const n = Math.max(1, Math.min(bands, span));
  const sizes = splitEvenly(span, n);
  let start = minAge;
  return sizes.map((size) => {
    const band = { minAge: start, maxAge: start + size - 1 };
    start += size;
    return band;
  });
};

/**
 * Suggest quotas that add up exactly to the wanted completes: equal age bands, gender split
 * from the Basics gender setting (Both = half/half, the odd complete going to male).
 */
export function autoDistribute({ ageMin, ageMax, gender, completes, bands = 4 }) {
  const ranges = ageBands(Number(ageMin), Number(ageMax), bands);
  const perBand = splitEvenly(Number(completes), ranges.length);
  return ranges.map((r, i) => {
    const total = perBand[i];
    let male = 0;
    let female = 0;
    if (gender === 'Male') male = total;
    else if (gender === 'Female') female = total;
    else { male = Math.ceil(total / 2); female = total - male; }
    return { key: newKey(), minAge: String(r.minAge), maxAge: String(r.maxAge), male: String(male), female: String(female), stateIds: [], profilingAnswerIds: [] };
  });
}

/**
 * The dimensions a row is split by. A switch that is off means "not split by that dimension", so the row's ids
 * for it are ignored (and not sent). Empty ids mean the whole target group for that dimension.
 */
const effectiveIds = (ids, on) => (on ? [...(ids || [])].sort() : []);
const cellKey = (r, { splitRegion = false, splitProfiling = false } = {}) =>
  `${effectiveIds(r.stateIds, splitRegion).join(',')}|${effectiveIds(r.profilingAnswerIds, splitProfiling).join(',')}`;

/**
 * Returns { errors: {rowKey|'_form': message}, warnings: [string] }.
 * `splits` = { splitRegion, splitProfiling }: age bands may only overlap between rows that are split differently
 * (the backend compares only rows with exactly the same regions and profiling answers).
 */
export function validateQuotas(rows, { ageMin, ageMax, wanted }, splits = {}) {
  const errors = {};
  const warnings = [];
  if (!rows.length) errors._form = 'Add at least one quota row.';

  const parsed = [];
  rows.forEach((r) => {
    const lo = int(r.minAge); const hi = int(r.maxAge);
    if (lo === null || hi === null) errors[r.key] = 'Enter whole-number ages.';
    else if (lo < LIMITS.MIN_AGE) errors[r.key] = `Minimum age is ${LIMITS.MIN_AGE}.`;
    else if (hi < lo) errors[r.key] = 'Max age is below min age.';
    else if (lo < Number(ageMin) || hi > Number(ageMax)) errors[r.key] = `Ages must stay within ${ageMin}-${ageMax} (the target group's age range).`;
    else if (int(r.male) === null || int(r.female) === null) errors[r.key] = 'Completes must be whole numbers (0 or more).';
    else parsed.push({ key: r.key, lo, hi, cell: cellKey(r, splits) });
  });

  // Age bands (bounds inclusive: 18-30 and 30-45 overlap) are compared within each cell only.
  const cells = {};
  parsed.forEach((p) => { (cells[p.cell] = cells[p.cell] || []).push(p); });
  Object.values(cells).forEach((group) => {
    const sorted = [...group].sort((a, b) => a.lo - b.lo);
    for (let i = 1; i < sorted.length; i += 1) {
      if (sorted[i].lo <= sorted[i - 1].hi) errors[sorted[i].key] = errors[sorted[i].key] || 'Age bands overlap.';
    }
  });

  const total = totalCompletes(rows);
  if (!errors._form && Number(wanted) > 0) {
    if (total > Number(wanted)) errors._form = `Quotas total ${total}, which is more than the ${wanted} wanted completes.`;
    else if (total < Number(wanted)) warnings.push(`Quotas total ${total} of ${wanted} wanted completes.`);
  }
  return { errors, warnings };
}

/**
 * QuotaSettings body (backend developer's reply): no `id` (a repeat POST updates in place); every row is its own
 * cell. `stateIds` / `profilingAnswerIds` are arrays of id strings, sent only for a dimension that is switched on;
 * empty or omitted means the whole target group for that dimension. `quota` is a free-text label.
 */
export const quotasToPayload = (rows, { splitRegion = false, splitProfiling = false } = {}) => ({
  quota: ['age', 'gender', splitRegion && 'region', splitProfiling && 'profiling'].filter(Boolean).join('-'),
  mode: 'interlocking',
  data: rows.map((r) => ({
    gender: { male: int(r.male) || 0, female: int(r.female) || 0 },
    age: { minAge: int(r.minAge), maxAge: int(r.maxAge) },
    completes: rowTotal(r),
    ...(splitRegion ? { stateIds: [...(r.stateIds || [])] } : {}),
    ...(splitProfiling ? { profilingAnswerIds: [...(r.profilingAnswerIds || [])] } : {}),
  })),
});

// Saved ids may come back as plain strings or as resolved records with an `id`.
const idsOf = (list) => (list || []).map((x) => (x && typeof x === 'object' ? x.id : x)).filter(Boolean);

export const quotasFromSaved = (saved) =>
  ((saved && saved.data) || []).map((d) => ({
    key: newKey(),
    minAge: String(d.age ? d.age.minAge : ''),
    maxAge: String(d.age ? d.age.maxAge : ''),
    male: String(d.gender ? d.gender.male : 0),
    female: String(d.gender ? d.gender.female : 0),
    stateIds: idsOf(d.stateIds),
    profilingAnswerIds: idsOf(d.profilingAnswerIds),
  }));

/** Which split switches to start with when quotas are reopened: on when any saved row uses that dimension. */
export const splitsFromSaved = (saved) => {
  const data = (saved && saved.data) || [];
  return {
    splitRegion: data.some((d) => idsOf(d.stateIds).length > 0),
    splitProfiling: data.some((d) => idsOf(d.profilingAnswerIds).length > 0),
  };
};

/**
 * The backend's 400 `errors` are strings that start with the row they refer to:
 *   "data[1].age: ...", "data[1].stateIds: ...", "data[0] and data[2]: age bands overlap", or no prefix (total).
 * Returns { byRow: { [row.key]: [message] }, general: [message] }. Row numbers are the order the rows were sent in.
 */
export function parseQuotaErrors(errors, rows) {
  const byRow = {};
  const general = [];
  const addToRow = (index, message, original) => {
    const row = rows[Number(index)];
    if (!row) { general.push(original); return; }
    (byRow[row.key] = byRow[row.key] || []).push(message);
  };
  (Array.isArray(errors) ? errors : []).forEach((text) => {
    const pair = /^data\[(\d+)\] and data\[(\d+)\]:\s*(.*)$/.exec(text);
    if (pair) { addToRow(pair[1], pair[3], text); addToRow(pair[2], pair[3], text); return; }
    const one = /^data\[(\d+)\](?:\.\w+)?:\s*(.*)$/.exec(text);
    if (one) addToRow(one[1], one[2], text);
    else general.push(text);
  });
  return { byRow, general };
}

// ---------------------------------------------------------------------------------------------------------------
// Quota mode (backend developer's message; the live Swagger did not list it yet when this was written)
//
//  interlocking     (default) every row is one CELL; rows are added up. Everything above.
//  nonInterlocking  every row is an INDEPENDENT quota on ONE dimension: an age band, regions (`stateIds`) or
//                   profiling answers (`profilingAnswerIds`); a gender split is allowed on any row. Bands may overlap
//                   and rows are not added up; no single row may exceed the wanted completes.
// `mode` is always returned. When it is missing, the old label "nonInterLockedQuota" means nonInterlocking.
// ---------------------------------------------------------------------------------------------------------------

export const INTERLOCKING = 'interlocking';
export const NON_INTERLOCKING = 'nonInterlocking';

export const modeFromSaved = (saved) => {
  if (!saved) return INTERLOCKING;
  if (saved.mode === NON_INTERLOCKING) return NON_INTERLOCKING;
  if (!saved.mode && saved.quota === 'nonInterLockedQuota') return NON_INTERLOCKING;
  return INTERLOCKING;
};

/** A blank independent quota: an age band by default. */
export const newLooseRow = (dimension = 'age') => ({
  key: newKey(), dimension, minAge: '', maxAge: '', male: '', female: '', completes: '', stateIds: [], profilingAnswerIds: [],
});

const filled = (v) => String(v === undefined || v === null ? '' : v).trim() !== '';
/** A gender split is used when either gender cell has something typed in it. */
export const looseHasGender = (r) => filled(r.male) || filled(r.female);
/** With a gender split the row's completes are male + female; otherwise the number typed in. */
export const looseCompletes = (r) => (looseHasGender(r) ? (int(r.male) || 0) + (int(r.female) || 0) : int(r.completes) || 0);
export const largestLooseQuota = (rows) => rows.reduce((max, r) => Math.max(max, looseCompletes(r)), 0);

/** Returns { errors: {rowKey|'_form': message}, warnings: [string] } for independent quotas. */
export function validateLooseQuotas(rows, { ageMin, ageMax, wanted }) {
  const errors = {};
  const warnings = [];
  if (!rows.length) errors._form = 'Add at least one quota.';

  rows.forEach((r) => {
    const dimension = r.dimension || 'age';
    if (dimension === 'age') {
      const lo = int(r.minAge); const hi = int(r.maxAge);
      if (lo === null || hi === null) { errors[r.key] = 'Enter whole-number ages.'; return; }
      if (lo < LIMITS.MIN_AGE) { errors[r.key] = `Minimum age is ${LIMITS.MIN_AGE}.`; return; }
      if (hi < lo) { errors[r.key] = 'Max age is below min age.'; return; }
      if (lo < Number(ageMin) || hi > Number(ageMax)) { errors[r.key] = `Ages must stay within ${ageMin}-${ageMax} (the target group's age range).`; return; }
    } else if (dimension === 'region') {
      if (!(r.stateIds || []).length) { errors[r.key] = 'Choose at least one region.'; return; }
    } else if (dimension === 'profiling') {
      if (!(r.profilingAnswerIds || []).length) { errors[r.key] = 'Choose at least one profiling answer.'; return; }
    } else if (!looseHasGender(r)) {
      errors[r.key] = 'Choose an age band, regions or profiling, or add a gender split.';
      return;
    }
    if (looseHasGender(r)) {
      if ((filled(r.male) && int(r.male) === null) || (filled(r.female) && int(r.female) === null)) { errors[r.key] = 'Gender completes must be whole numbers (0 or more).'; return; }
    } else if (int(r.completes) === null) {
      errors[r.key] = 'Completes must be a whole number (0 or more).';
      return;
    }
    if (Number(wanted) > 0 && looseCompletes(r) > Number(wanted)) {
      errors[r.key] = `${looseCompletes(r)} is more than the ${wanted} wanted completes. No single quota may exceed it.`;
    }
  });
  return { errors, warnings };
}

/** The label saved with independent quotas: the dimensions the rows use, e.g. "age and gender". */
const looseLabel = (rows) => {
  const used = [];
  if (rows.some((r) => (r.dimension || 'age') === 'age')) used.push('age');
  if (rows.some((r) => r.dimension === 'region')) used.push('region');
  if (rows.some((r) => r.dimension === 'profiling')) used.push('profiling');
  if (rows.some(looseHasGender)) used.push('gender');
  return used.length ? used.join(' and ') : 'quota';
};

/** Independent-quota body: each row carries only its own dimension, plus a gender split when one is used. */
export const looseQuotasToPayload = (rows) => ({
  quota: looseLabel(rows),
  mode: NON_INTERLOCKING,
  data: rows.map((r) => {
    const dimension = r.dimension || 'age';
    return {
      ...(looseHasGender(r) ? { gender: { male: int(r.male) || 0, female: int(r.female) || 0 } } : {}),
      ...(dimension === 'age' ? { age: { minAge: int(r.minAge), maxAge: int(r.maxAge) } } : {}),
      ...(dimension === 'region' ? { stateIds: [...(r.stateIds || [])] } : {}),
      ...(dimension === 'profiling' ? { profilingAnswerIds: [...(r.profilingAnswerIds || [])] } : {}),
      completes: looseCompletes(r),
    };
  }),
});

export const looseFromSaved = (saved) =>
  ((saved && saved.data) || []).map((d) => {
    const states = idsOf(d.stateIds);
    const answers = idsOf(d.profilingAnswerIds);
    const dimension = d.age ? 'age' : states.length ? 'region' : answers.length ? 'profiling' : 'none';
    return {
      key: newKey(),
      dimension,
      minAge: d.age && d.age.minAge != null ? String(d.age.minAge) : '',
      maxAge: d.age && d.age.maxAge != null ? String(d.age.maxAge) : '',
      male: d.gender && d.gender.male != null ? String(d.gender.male) : '',
      female: d.gender && d.gender.female != null ? String(d.gender.female) : '',
      completes: d.gender ? '' : String(d.completes != null ? d.completes : ''),
      stateIds: states,
      profilingAnswerIds: answers,
    };
  });
